import { decodePageInfo, isRecord, requireBoolean, requireNumber, requireString } from '@/lib/api'

export type AdHocQueryLogicalType = 'string' | 'integer' | 'float' | 'boolean' | 'timestamp' | 'json' | 'unknown'
export type QueryCell = string | number | boolean | null

export type ReportRunColumn = { name: string; type: AdHocQueryLogicalType }

export type ReportRunResult = {
  columns: ReportRunColumn[]
  rows: QueryCell[][]
  rowCount: number
  durationMs: number
  truncated: boolean
  pageInfo?: ReturnType<typeof decodePageInfo>
}

export function decodeAdHocQueryLogicalType(raw: unknown, path: string): AdHocQueryLogicalType {
  const type = requireString(raw, path)
  switch (type) {
    case 'string':
    case 'integer':
    case 'float':
    case 'boolean':
    case 'timestamp':
    case 'json':
    case 'unknown':
      return type
    default:
      throw new Error(`Unexpected query result type "${type}" at ${path}`)
  }
}

export function decodeQueryCell(raw: unknown, path: string, type: AdHocQueryLogicalType): QueryCell {
  if (raw === null) return null
  if (type === 'boolean' && typeof raw === 'boolean') return raw
  if (type === 'float' && typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (['string', 'integer', 'float', 'timestamp', 'json', 'unknown'].includes(type) && typeof raw === 'string') return raw
  throw new Error(`Unexpected query result value at ${path}`)
}

export function decodeReportRun(raw: unknown): ReportRunResult {
  if (!isRecord(raw)) throw new Error('Malformed report result response')
  if (!Array.isArray(raw.columns)) throw new Error('Expected an array for "columns"')
  if (!Array.isArray(raw.rows)) throw new Error('Expected an array for "rows"')
  const columns = raw.columns.map((column, index) => {
    if (!isRecord(column)) throw new Error(`Malformed report column at columns.${index}`)
    return {
      name: requireString(column.name, `columns.${index}.name`),
      type: decodeAdHocQueryLogicalType(column.type, `columns.${index}.type`),
    } satisfies ReportRunColumn
  })
  const rows = raw.rows.map((row, rowIndex) => {
    if (!Array.isArray(row)) throw new Error(`Expected an array for "rows.${rowIndex}"`)
    if (row.length !== columns.length) throw new Error(`Expected ${columns.length} values for "rows.${rowIndex}"`)
    return columns.map((column, cellIndex) => decodeQueryCell(row[cellIndex], `rows.${rowIndex}.${cellIndex}`, column.type))
  })
  return {
    columns,
    rows,
    rowCount: requireNumber(raw.rowCount, 'rowCount'),
    durationMs: requireNumber(raw.durationMs, 'durationMs'),
    truncated: requireBoolean(raw.truncated, 'truncated'),
    ...(raw.pageInfo == null ? {} : { pageInfo: decodePageInfo(raw.pageInfo) }),
  } satisfies ReportRunResult
}
